import React from 'react';
import { Theme, useTheme } from '@mui/material/styles';
import { LocalAudioTrack, LocalVideoTrack, Participant, RemoteAudioTrack, RemoteVideoTrack } from 'twilio-video';
import { styled } from '@mui/material/styles';

import AudioLevelIndicator from '../AudioLevelIndicator/AudioLevelIndicator';
import NetworkQualityLevel from '../NetworkQualityLevel/NetworkQualityLevel';
import PinIcon from './PinIcon/PinIcon';
import ScreenShareIcon from '../../icons/ScreenShareIcon';
import Typography from '@mui/material/Typography';

import useIsTrackSwitchedOff from '../../hooks/useIsTrackSwitchedOff/useIsTrackSwitchedOff';
import usePublications from '../../hooks/usePublications/usePublications';
import useTrack from '../../hooks/useTrack/useTrack';
import useParticipantIsReconnecting from '../../hooks/useParticipantIsReconnecting/useParticipantIsReconnecting';
import { useAppState } from '../../state';

import { getIdentityInitials, removeIdentityMasking } from '../../utils';

const borderWidth = 2;

const Container = styled('div', {
  shouldForwardProp: (prop) =>
    prop !== 'hideParticipant' &&
    prop !== 'isCursorPointer' &&
    prop !== 'isDominantSpeaker' &&
    prop !== 'isGalleryViewActive',
})<{
  hideParticipant?: boolean;
  isCursorPointer?: boolean;
  isDominantSpeaker?: boolean;
  isGalleryViewActive?: boolean;
}>(({ theme, hideParticipant, isCursorPointer, isDominantSpeaker, isGalleryViewActive }) => ({
  width: '204px',
  marginRight: '8px',
  marginBottom: '0',
  paddingTop: `calc(${theme.sidebarHeight}px - ${theme.participantBorderWidth}px)`,
  height: 'inherit',
  isolation: 'isolate',
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
  overflow: 'hidden',
  '& video': {
    objectFit: 'contain !important',
  },
  borderRadius: '10px',
  border: `${theme.participantBorderWidth}px solid rgb(119, 119, 119)`,
  background: 'black',
  [theme.breakpoints.down('sm')]: {
    height: theme.sidebarMobileHeight,
    width: `${(theme.sidebarMobileHeight * 16) / 9}px`,
    marginRight: '8px',
    marginBottom: '0',
    fontSize: '12px',
    paddingTop: `${theme.sidebarMobileHeight - theme.participantBorderWidth}px`,
  },
  ...(hideParticipant && {
    display: 'none',
  }),
  ...(isCursorPointer && {
    cursor: 'pointer',
  }),
  ...(isDominantSpeaker && {
    border: `solid ${borderWidth}px #7BEAA5`,
  }),
  ...(isGalleryViewActive && {
    width: 'auto',
    border: `${theme.participantBorderWidth}px solid ${theme.galleryViewBackgroundColor}`,
    borderRadius: '8px',
    [theme.breakpoints.down('sm')]: {
      position: 'relative',
      width: '100%',
      height: '100%',
      padding: '0',
      fontSize: '12px',
      margin: '0',
      '& video': {
        objectFit: 'cover !important',
      },
    },
  }),
}));

const InnerContainer = styled('div')({
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
});

const InfoContainer = styled('div')({
  position: 'absolute',
  zIndex: 2,
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
  height: '100%',
  width: '100%',
  background: 'transparent',
  top: 0,
});

const AvatarContainer = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: '#191b1d',
  position: 'absolute',
  color: 'white',
  fontSize: 'xxx-large',
  top: 0,
  right: 0,
  bottom: 0,
  left: 0,
  zIndex: 1,
  [theme.breakpoints.down('sm')]: {
    '& svg': {
      transform: 'scale(0.7)',
    },
  },
}));

const ReconnectingContainer = styled('div')({
  position: 'absolute',
  top: 0,
  right: 0,
  bottom: 0,
  left: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: 'rgba(40, 42, 43, 0.75)',
  zIndex: 1,
});

const ScreenShareIconContainer = styled('span')({
  background: 'rgba(0, 0, 0, 0.5)',
  padding: '0.18em 0.3em',
  marginRight: '0.3em',
  display: 'flex',
  '& path': {
    fill: 'white',
  },
});

const Identity = styled('span')({
  background: 'rgba(0, 0, 0, 0.5)',
  color: 'white',
  padding: '0.18em 0.3em 0.18em 0',
  margin: 0,
  display: 'flex',
  alignItems: 'center',
  borderRadius: '10px',
});

const IdentityType = styled('span')({
  background: '#FFA85C',
  color: 'white',
  margin: 0,
  display: 'flex',
  alignItems: 'center',
  borderRadius: '10px',
  justifyContent: 'center',
  minWidth: '36px',
  maxHeight: '19px',
});

const InfoRowBottom = styled('div')({
  display: 'flex',
  justifyContent: 'space-between',
  position: 'absolute',
  bottom: 0,
  left: 0,
  alignItems: 'center',
});



interface ParticipantInfoProps {
  participant: Participant;
  children: React.ReactNode;
  onClick?: () => void;
  isSelected?: boolean;
  isLocalParticipant?: boolean;
  hideParticipant?: boolean;
  isDominantSpeaker?: boolean;
}

export default function ParticipantInfo({
  participant,
  onClick,
  isSelected,
  children,
  isLocalParticipant,
  hideParticipant,
  isDominantSpeaker,
}: ParticipantInfoProps) {
  const publications = usePublications(participant);

  const audioPublication = publications.find(p => p.kind === 'audio');
  const videoPublication = publications.find(p => !p.trackName.includes('screen') && p.kind === 'video');

  const isVideoEnabled = Boolean(videoPublication);
  const isScreenShareEnabled = publications.find(p => p.trackName.includes('screen'));

  const videoTrack = useTrack(videoPublication);
  const isVideoSwitchedOff = useIsTrackSwitchedOff(videoTrack as LocalVideoTrack | RemoteVideoTrack);

  const audioTrack = useTrack(audioPublication) as LocalAudioTrack | RemoteAudioTrack | undefined;
  const isParticipantReconnecting = useParticipantIsReconnecting(participant);

  const { isGalleryViewActive } = useAppState();

  return (
    <Container
      hideParticipant={hideParticipant}
      isCursorPointer={Boolean(onClick)}
      isDominantSpeaker={isDominantSpeaker}
      isGalleryViewActive={isGalleryViewActive}
      onClick={onClick}
      data-cy-participant={participant.identity}
    >
      <InfoContainer>
        <NetworkQualityLevel participant={participant} />
        <InfoRowBottom>
          {isScreenShareEnabled && (
            <ScreenShareIconContainer>
              <ScreenShareIcon />
            </ScreenShareIconContainer>
          )}
          <Identity>
            <AudioLevelIndicator audioTrack={audioTrack} />
            <Typography
              variant="body1"
              component="span"
              sx={{
                color: 'white',
                fontSize: '0.7rem',
              }}
            >
              {removeIdentityMasking(participant.identity)}
            </Typography>
          </Identity>
          <IdentityType>
            <Typography
              variant="body1"
              component="span"
              sx={{
                color: 'white',
                fontSize: '0.7rem',
              }}
            >
              {isLocalParticipant && 'ME'}
            </Typography>
          </IdentityType>
        </InfoRowBottom>
        <div style={{ position: 'absolute', bottom: '0px', right: '0px' }}>
          {isSelected && <PinIcon />}
        </div>
      </InfoContainer>
      <InnerContainer>
        {(!isVideoEnabled || isVideoSwitchedOff) && (
          <AvatarContainer>
            {getIdentityInitials(participant.identity)}
          </AvatarContainer>
        )}
        {isParticipantReconnecting && (
          <ReconnectingContainer>
            <Typography
              variant="body1"
              sx={{
                color: 'white',
                fontSize: '0.7rem',
              }}
            >
              Reconnecting...
            </Typography>
          </ReconnectingContainer>
        )}
        {children}
      </InnerContainer>
    </Container>
  );
}
